/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Observable, Subject, NEVER, ReplaySubject } from 'rxjs';
import { filter, startWith, tap } from 'rxjs/operators';
import { MultipleEventStream } from 'utils/lib/event-stream/event-streams';
import { BridgeService } from '../bridge/bridge.service';
import { Logger } from '../logging/logging.service';
import { Types } from './models/handler';

interface EventStreamKey<KeyType> {
    eventKey: KeyType;
    messageType: string;
}

export type NotObject = Exclude<keyof any, keyof object>;

export abstract class EventsHandlerService<KeyType extends NotObject, InternalType, ValueType> {
    private eventHandlerLogger: Logger;
    protected handlerMap: Types.HandlerMap<KeyType, InternalType, ValueType>;
    protected defaultHandlerType: string;
    private eventStreams: MultipleEventStream<KeyType, ValueType>;
    private persistentEventStreams: MultipleEventStream<KeyType, ValueType>;
    private keyToMessageType: Map<KeyType, string> = new Map<KeyType, string>();

    constructor(
        protected bridgeService: BridgeService,
        logger: Logger
    ) {
        this.eventHandlerLogger = logger;
        this.eventStreams = new MultipleEventStream<KeyType, ValueType>(
            (eventStreamKey: KeyType) => {
                if (!this.keyToMessageType.has(eventStreamKey)) {
                    this.eventHandlerLogger.error('No message type for key', eventStreamKey);
                    return NEVER;
                }

                const messageTypeForKey = this.keyToMessageType.get(eventStreamKey);
                this.handlerMap[messageTypeForKey].internalSubjects.set(eventStreamKey, new Subject<ValueType>());
                return this.handlerMap[messageTypeForKey].internalSubjects.get(eventStreamKey).asObservable();
            },
            false,
            false
        );
        this.persistentEventStreams = new MultipleEventStream<KeyType, ValueType>(
            (eventStreamKey: KeyType) => {
                if (!this.keyToMessageType.has(eventStreamKey)) {
                    this.eventHandlerLogger.error('No message type for key', eventStreamKey);
                    return NEVER;
                }

                const messageTypeForKey = this.keyToMessageType.get(eventStreamKey);
                // Some events are emitted before the observable is created or before observers are attached.
                // We store pending events in the handlerMap and emit them when the observable is created and observers are attached.
                // ReplaySubject is used because some observables have multiple observers.
                // In particular, this is needed for URL_ROUTE_CHANGE on Mac OS to handle launching UI from a desktop shortcut.
                this.handlerMap[messageTypeForKey].persistentInternalSubjects.set(
                    eventStreamKey,
                    new ReplaySubject<ValueType>(1)
                );

                return this.handlerMap[messageTypeForKey].persistentInternalSubjects
                    .get(eventStreamKey)
                    .asObservable()
                    .pipe(
                        startWith(null), // Signal the beginning of the queue with null
                        tap(value => {
                            if (value === null) {
                                // Process queued events on the initial null
                                const copyEvents = this.handlerMap[messageTypeForKey].queuedEvents.slice();
                                this.handlerMap[messageTypeForKey].queuedEvents = [];

                                for (const event of copyEvents) {
                                    this.processMessage(event, true);
                                }
                            }
                        }),
                        filter(value => value !== null) // Stop processing the signalling null
                    );
            },
            false,
            false
        );
    }

    protected registerNewHandler(
        eventType: string,
        pred: Types.HandlerPredicate,
        key: Types.KeyExtractor<InternalType, KeyType>,
        val: Types.ValueExtractor<InternalType, ValueType>,
        transform: Types.TransformedData<InternalType>
    ) {
        if (!this.handlerMap) {
            this.handlerMap = {};
        }
        this.handlerMap[eventType] = {
            pred,
            keyFunc: key,
            valFunc: val,
            transformedFunc: transform,
            internalSubjects: new Map<KeyType, Subject<ValueType>>(),
            persistentInternalSubjects: new Map<KeyType, Subject<ValueType>>(),
            queuedEvents: []
        };
    }

    get$(
        key: KeyType,
        messageType: string = this.defaultHandlerType,
        persistent: boolean = false
    ): Observable<ValueType> {
        if (!this.keyToMessageType.has(key)) {
            this.keyToMessageType.set(key, messageType);
        }

        if (persistent) {
            return this.persistentEventStreams.get$(key);
        } else {
            return this.eventStreams.get$(key);
        }
    }

    protected processMessage(message: string, isRepeatedMessage: boolean = false) {
        // Log of message commented out for production because it may contain
        // user authentication tokens
        // this.eventHandlerLogger.info('Process Message: ', message);
        for (const handlerType of Object.keys(this.handlerMap)) {
            const predData: boolean = this.handlerMap[handlerType].pred(message);
            if (predData) {
                const transformData: InternalType = this.handlerMap[handlerType].transformedFunc(message);
                const key = this.handlerMap[handlerType].keyFunc(transformData);
                const val = this.handlerMap[handlerType].valFunc(transformData);
                if (
                    this.handlerMap[handlerType].internalSubjects.has(key) ||
                    this.handlerMap[handlerType].persistentInternalSubjects.has(key)
                ) {
                    if (!isRepeatedMessage && this.handlerMap[handlerType].internalSubjects.has(key)) {
                        this.handlerMap[handlerType].internalSubjects.get(key).next(val);
                    }
                    if (this.handlerMap[handlerType].persistentInternalSubjects.has(key)) {
                        this.handlerMap[handlerType].persistentInternalSubjects.get(key).next(val);
                    }
                } else {
                    this.eventHandlerLogger.info(
                        'No subscribers for event with key ',
                        key,
                        '. Adding the event to queue.'
                    );
                    this.handlerMap[handlerType].queuedEvents.push(message);
                }
            }
        }
    }
}
